'use client';

import { useState } from 'react';
import { AlertTriangle, Info, X, CreditCard } from 'lucide-react';
import { useSubscription } from './SubscriptionContext';
import { Button } from './Button';
import { getApiBase } from '@/lib/apiBase';

export function PaymentWarningBanner() {
  const { paymentFailed, cancelAtPeriodEnd, currentPeriodEnd, tier } = useSubscription();
  const [dismissedPayment, setDismissedPayment] = useState(false);
  const [dismissedCancel, setDismissedCancel] = useState(false);

  const handleManagePayment = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/stripe/create-portal`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const showPaymentWarning = paymentFailed && tier === 'paid' && !dismissedPayment;
  const showCancelInfo = cancelAtPeriodEnd && tier === 'paid' && !dismissedCancel && !showPaymentWarning;

  if (!showPaymentWarning && !showCancelInfo) {
    return null;
  }

  if (showPaymentWarning) {
    return (
      <div className="bg-amber-500/10 border-b border-amber-500/30">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                <span className="font-medium">Payment failed.</span>{' '}
                Please update your payment method to keep your premium subscription active.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleManagePayment}
                className="whitespace-nowrap"
              >
                <CreditCard className="w-4 h-4 mr-1.5" />
                Update Payment
              </Button>
              <button
                onClick={() => setDismissedPayment(true)}
                className="p-1.5 text-amber-400 hover:text-amber-300 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formattedDate = currentPeriodEnd
    ? currentPeriodEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div className="bg-blue-500/10 border-b border-blue-500/30">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-200">
              Your Premium subscription will end on <span className="font-medium">{formattedDate}</span>. You can resubscribe anytime.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleManagePayment}
              className="whitespace-nowrap"
            >
              Manage Subscription
            </Button>
            <button
              onClick={() => setDismissedCancel(true)}
              className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
